/**
 * File: useProjectContext.ts
 * Description: Hook for managing project-specific context including title
 * Author: System
 * Created: 2025-09-26
 * Last Modified: 2025-09-26
 */

import { useEffect, useState } from 'react';

import { contextManager } from '../services/contextManager';
import { projectService } from '../services/projectService';
import { ProjectItem } from '../types/project';
import { logger } from '../utils/logger';

/**
 * Hook to manage project-specific context including project title
 */
export const useProjectContext = (projectId: number | null) => {
  const [project, setProject] = useState<ProjectItem | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadProjectAndUpdateContext = async () => {
      if (!projectId) {
        // Clear project-related context when no project ID
        contextManager.removeContext('project_title');
        setProject(null);
        setError(null);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const projectData = await projectService.getProject(projectId);
        
        // Update context with project title
        contextManager.updateContext(
          'project_title', 
          projectData.title, 
          'project_data',
        );
        
        setProject(projectData);
      } catch (err) {
        const errorMessage = err instanceof Error 
          ? err.message 
          : 'Failed to load project data';
        
        setError(errorMessage);
        
        // Clear project title from context on error
        contextManager.removeContext('project_title');
        setProject(null);
        
        logger.error('Error loading project context:', err);
      } finally {
        setIsLoading(false);
      }
    };

    loadProjectAndUpdateContext();
  }, [projectId]);

  return {
    project,
    isLoading,
    error,
  };
};